import { isTouchDevice } from '../../lib/use-mobile.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BASE_PATH } from '@ai-studio/shared';

import type { EngineView } from '../../engine/prism.js';
import { arcadeSaveHost } from '../../engine/saves.js';
import { ApiError, api } from '../../lib/api.js';
import { Viewport } from '../Viewport.js';
import { Link, signInUrl, type SiteUser } from './WebsiteApp.js';

export interface LibraryGame {
  slug: string;
  title: string;
  description: string;
  version: number;
  multiplayer: boolean;
  /** `relay`: join codes; `server`: the game's own server, run by its creator outside this site. */
  network: 'relay' | 'server' | null;
  thumbnail: boolean;
  plays: number;
  featured: boolean;
  author: string;
  authorAvatar: string | null;
  updatedAt: string;
  createdAt: string;
}

export function GameCard({ game }: { game: LibraryGame }) {
  return (
    <Link to={`/games/${game.slug}`} className="game-card">
      <div className="game-thumb">
        {game.thumbnail ? <img src={`${BASE_PATH}/play-thumb/${game.slug}?v=${game.version}`} alt="" loading="lazy" /> : <span className="game-thumb-empty" aria-hidden="true">▶</span>}
        {game.multiplayer && <span className="game-tag">Multiplayer</span>}
        {game.featured && <span className="game-tag game-tag-featured">Featured</span>}
      </div>
      <div className="game-info">
        <strong>{game.title}</strong>
        <span className="muted">
          {game.author} · {game.plays.toLocaleString()} plays
        </span>
      </div>
    </Link>
  );
}

/** The public game library (the site's home page). */
export function LibraryPage({ user }: { user: SiteUser | null }) {
  const [games, setGames] = useState<LibraryGame[] | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'new' | 'popular' | 'updated'>('new');
  const [multiplayer, setMultiplayer] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = new URLSearchParams({ sort, ...(search ? { search } : {}), ...(multiplayer ? { multiplayer: '1' } : {}) });
      api
        .get<{ games: LibraryGame[] }>(`/library/games?${q.toString()}`)
        .then((r) => {
          setGames(r.games);
          setError(null);
        })
        .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not load the library.'));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [search, sort, multiplayer]);

  return (
    <div className="site-page">
      <section className="arcade-hero">
        <p className="eyebrow">The arcade</p>
        <h1>
          Games made with <span className="spectrum-text">Prism Engine Cloud</span>
        </h1>
        <p className="hero-sub">Play in your browser. Multiplayer games join with a code, no setup needed.</p>
        {!user && (
          <p>
            <a className="btn btn-primary" href={signInUrl(`${BASE_PATH}/projects`)}>
              Make your own
            </a>
          </p>
        )}
      </section>
      <div className="library-tools">
        <input type="search" placeholder="Search games" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label="Sort">
          <option value="new">Newest</option>
          <option value="popular">Most played</option>
          <option value="updated">Recently updated</option>
        </select>
        <label className="field-inline">
          <input type="checkbox" checked={multiplayer} onChange={(e) => setMultiplayer(e.target.checked)} />
          <span>Multiplayer only</span>
        </label>
      </div>
      {error && <p className="login-error">{error}</p>}
      {games === null ? (
        <p className="muted">Loading…</p>
      ) : games.length === 0 ? (
        <p className="muted">No games yet{search ? ' match that search' : ''}.</p>
      ) : (
        <div className="game-grid">
          {games.map((g) => (
            <GameCard key={g.slug} game={g} />
          ))}
        </div>
      )}
    </div>
  );
}

interface GameDetail {
  game: LibraryGame;
  mine: boolean;
  files: string;
}

/** One game: its page, and the player (the site's own engine, the game's files). */
export function GamePage({ slug, user }: { slug: string; user: SiteUser | null }) {
  const [detail, setDetail] = useState<GameDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [startScene, setStartScene] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<'landscape' | 'portrait' | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const filesRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    setPlaying(false);
    api
      .get<GameDetail>(`/library/games/${slug}`)
      .then(async (d) => {
        // The game's file list (files.json): the engine asks for optional files (model sidecars, import
        // guesses) that most games lack; unlisted ones are answered "not found" without a request.
        const list = (await fetch(`${d.files}files.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null)) as { files?: unknown } | null;
        filesRef.current = Array.isArray(list?.files) ? new Set(list.files.filter((f): f is string => typeof f === 'string')) : null;
        setDetail(d);
        const info = (await fetch(`${d.files}game.json`).then((r) => r.json()).catch(() => ({}))) as { startScene?: string; orientation?: string };
        setStartScene(info.startScene ?? null);
        setOrientation(info.orientation === 'landscape' || info.orientation === 'portrait' ? info.orientation : null);
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.status === 404 ? 'This game does not exist (any more).' : 'Could not load the game.'));
  }, [slug]);

  const source = useMemo(
    () =>
      detail
        ? {
            key: `${detail.game.slug}@${detail.game.version}`,
            // Saves stay in this browser per game (not per version: a republish keeps your worlds).
            saves: arcadeSaveHost(`game:${detail.game.slug}`),
            readFile: async (path: string) => {
              const clean = path.replace(/\\/g, '/').replace(/^\.?\/+/, '');
              // (shared model textures may be newer than the list: the site shares them in the background)
              if (filesRef.current && !filesRef.current.has(clean) && !clean.startsWith('textures/glb/')) throw new Error(`${clean}: not found`);
              const res = await fetch(`${detail.files}data/${clean.split('/').map(encodeURIComponent).join('/')}`);
              if (!res.ok) throw new Error(`${clean}: ${res.status === 404 ? 'not found' : `HTTP ${res.status}`}`);
              return new Uint8Array(await res.arrayBuffer());
            },
          }
        : undefined,
    [detail],
  );

  if (error) return <div className="site-page"><p className="login-error">{error}</p><Link to="/games">Back to the arcade</Link></div>;
  if (!detail) return <div className="site-page"><p className="muted">Loading…</p></div>;
  const g = detail.game;

  async function start(view: EngineView) {
    // Nobody reads the Game Trace here (the editor's assistant does): recording it costs ~1 ms a frame.
    view.traceConfig({ enabled: false, level: 'normal' });
    if (startScene) await view.openScene(startScene);
    await view.play();
  }

  return (
    <div className="site-page game-page">
      <div className="game-head">
        <div>
          <h1>{g.title}</h1>
          <p className="muted">
            by {g.author} · version {g.version} · {g.plays.toLocaleString()} plays
            {g.network === 'server' ? " · online (connects to the creator's own server)" : g.multiplayer ? ' · multiplayer (join codes, up to 8 players)' : ''}
          </p>
        </div>
        <div className="game-actions">
          {playing && (
            <button className="ghost" type="button" onClick={() => void stageRef.current?.requestFullscreen?.()}>
              Fullscreen
            </button>
          )}
          {playing ? (
            <button className="ghost" type="button" onClick={() => setPlaying(false)}>
              Stop
            </button>
          ) : (
            <button
              className="primary"
              type="button"
              onClick={() => {
                setPlaying(true);
                void api.post(`/library/games/${g.slug}/play`).catch(() => undefined);
                // Phones and tablets: straight to full screen, turned the way the game is made.
                if (isTouchDevice()) {
                  void stageRef.current
                    ?.requestFullscreen?.({ navigationUI: 'hide' })
                    .then(() => (orientation ? (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.(orientation) : undefined))
                    .catch(() => undefined);
                }
              }}
            >
              Play
            </button>
          )}
        </div>
      </div>
      <div className="game-stage" ref={stageRef}>
        {playing && source ? (
          <Viewport mode="play" source={source} onReady={start} className="game-viewport" />
        ) : g.thumbnail ? (
          <img className="game-cover" src={`${BASE_PATH}/play-thumb/${g.slug}?v=${g.version}`} alt="" />
        ) : (
          <div className="game-cover game-cover-empty">
            <span className="game-thumb-empty" aria-hidden="true">▶</span>
          </div>
        )}
      </div>
      {g.description && <p className="game-description">{g.description}</p>}
      {detail.mine && user && <p className="muted">This is your game. Publish a new version from its project, or manage it under Account.</p>}
    </div>
  );
}
